import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { arretsMaladieAPI, validationsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  Upload, X, FileText, Eye, Trash2, CheckCircle2, XCircle,
  Activity, ArrowLeft, Plus, Calendar, AlertCircle, MessageSquare,
} from 'lucide-react';

function FileBadge({ file, onRemove }) {
  const isPdf = file.type === 'application/pdf' || file.name?.toLowerCase().endsWith('.pdf');
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '8px 12px', borderRadius: 9,
      background: 'var(--bg-elevated)', border: '1px solid var(--border)',
    }}>
      <FileText size={14} style={{ color: isPdf ? '#DC2626' : '#0891B2', flexShrink: 0 }} />
      <span style={{ fontSize: '0.8rem', color: 'var(--text-primary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {file.name}
      </span>
      {isPdf && (
        <button type="button" onClick={() => window.open(URL.createObjectURL(file), '_blank')}
          title="Aperçu"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0 }}>
          <Eye size={13} />
        </button>
      )}
      {onRemove && (
        <button type="button" onClick={onRemove}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626', padding: 0 }}>
          <X size={13} />
        </button>
      )}
    </div>
  );
}

function UploadedFileBadge({ justif, arretId, onDeleted, canDelete }) {
  const isPdf = justif.file_mime === 'application/pdf' || justif.file_name?.toLowerCase().endsWith('.pdf');
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    if (!window.confirm('Supprimer ce justificatif ?')) return;
    setLoading(true);
    try {
      await arretsMaladieAPI.deleteJustificatif(arretId, justif.id);
      onDeleted(justif.id);
      toast.success('Justificatif supprimé');
    } catch { toast.error('Erreur suppression'); }
    finally { setLoading(false); }
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '8px 12px', borderRadius: 9,
      background: 'var(--bg-elevated)', border: '1px solid var(--border)',
    }}>
      <FileText size={14} style={{ color: isPdf ? '#DC2626' : '#0891B2', flexShrink: 0 }} />
      <span style={{ fontSize: '0.8rem', color: 'var(--text-primary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {justif.file_name}
      </span>
      {justif.file_url && (
        <button type="button" onClick={() => window.open(justif.file_url, '_blank')} title="Voir"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0 }}>
          <Eye size={13} />
        </button>
      )}
      {canDelete && (
        <button type="button" onClick={handleDelete} disabled={loading}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626', padding: 0 }}>
          {loading ? '…' : <Trash2 size={13} />}
        </button>
      )}
    </div>
  );
}


export default function ArretMaladieForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin  = ['super_admin', 'admin', 'rh'].includes(user?.role?.toLowerCase());
  const isView   = !!id;

  const fileRef = useRef();

  const [arret,       setArret]       = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [fetching,    setFetching]    = useState(isView);

  // Form state
  const [dateDebut,   setDateDebut]   = useState('');
  const [dateFin,     setDateFin]     = useState('');
  const [departMidi,  setDepartMidi]  = useState(false);
  const [retourMidi,  setRetourMidi]  = useState(false);
  const [description, setDescription] = useState('');
  const [files,       setFiles]       = useState([]);

  // Validation (admin only)
  const [showValidModal, setShowValidModal] = useState(false);
  const [validAction,    setValidAction]    = useState(null); // 'VALIDE' | 'REJETE'
  const [commentaire,    setCommentaire]    = useState('');

  useEffect(() => {
    if (!id) return;
    arretsMaladieAPI.get(id)
      .then(r => {
        setArret(r.data);
        setDateDebut(r.data.date_debut || '');
        setDateFin(r.data.date_fin || '');
        setDepartMidi(r.data.depart_midi || false);
        setRetourMidi(r.data.retour_midi || false);
        setDescription(r.data.description || '');
      })
      .catch(() => toast.error('Arrêt introuvable'))
      .finally(() => setFetching(false));
  }, [id]);

  const addFiles = (newFiles) => {
    setFiles(prev => [...prev, ...Array.from(newFiles)]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!dateDebut || !dateFin) return toast.error('Les dates sont obligatoires');
    if (!isView && files.length === 0) return toast.error('Au moins un justificatif est obligatoire');
    if (dateFin < dateDebut) return toast.error('La date de fin doit être après la date de début');

    const form = new FormData();
    form.append('date_debut', dateDebut);
    form.append('date_fin', dateFin);
    form.append('depart_midi', departMidi);
    form.append('retour_midi', retourMidi);
    if (description) form.append('description', description);
    files.forEach(f => form.append('files', f));

    setLoading(true);
    try {
      await arretsMaladieAPI.create(form);
      toast.success('Arrêt maladie déposé');
      navigate('/arrets-maladie');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors du dépôt');
    } finally {
      setLoading(false);
    }
  };

  const handleAddJustificatif = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const form = new FormData();
    form.append('file', f);
    try {
      const res = await arretsMaladieAPI.addJustificatif(id, form);
      setArret(prev => ({ ...prev, justificatifs: [...(prev.justificatifs || []), res.data] }));
      toast.success('Justificatif ajouté');
    } catch { toast.error('Erreur upload'); }
  };

  const handleValidate = async () => {
    const form = new FormData();
    if (commentaire) form.append('commentaire_rh', commentaire);
    try {
      if (validAction === 'VALIDE') {
        await arretsMaladieAPI.validate(id, form);
        toast.success('Arrêt validé — feuille de temps mise à jour');
      } else {
        await arretsMaladieAPI.refuse(id, form);
        toast.success('Arrêt rejeté');
      }
      navigate('/arrets-maladie');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur validation');
    }
  };

  const inpStyle = {
    width: '100%', padding: '10px 12px', borderRadius: 8, boxSizing: 'border-box',
    border: '1.5px solid var(--border)', background: 'var(--bg-elevated)',
    color: 'var(--text-primary)', fontSize: '0.875rem', outline: 'none',
  };

  const labelStyle = {
    display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)',
    textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6,
  };

  if (fetching) {
    return <div style={{ textAlign: 'center', padding: 80, color: 'var(--text-muted)' }}>Chargement…</div>;
  }

  const statut = arret?.statut;

  return (
    <div style={{ padding: '28px 40px', maxWidth: 700, margin: '0 auto' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
        <button onClick={() => navigate('/arrets-maladie')}
          style={{ width: 34, height: 34, borderRadius: 8, border: '1px solid var(--border)',
                   background: 'var(--bg-card)', cursor: 'pointer', color: 'var(--text-muted)',
                   display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ArrowLeft size={16} />
        </button>
        <div style={{ width: 36, height: 36, borderRadius: 9, background: 'rgba(220,38,38,0.12)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Activity size={18} color="#DC2626" />
        </div>
        <div>
          <h1 style={{ fontSize: '1.25rem', fontWeight: 800, margin: 0, color: 'var(--text-primary)' }}>
            {isView ? 'Arrêt maladie' : 'Nouvel arrêt maladie'}
          </h1>
          {isView && arret && (
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2 }}>
              {arret.date_debut} → {arret.date_fin} · {arret.nb_jours}j
            </div>
          )}
        </div>
        {isView && statut && (
          <div style={{ marginLeft: 'auto' }}>
            {statut === 'VALIDE' && <span style={{ display:'flex', alignItems:'center', gap:5, color:'#059669', fontWeight:700, fontSize:'0.8rem' }}><CheckCircle2 size={14}/> Validé</span>}
            {statut === 'SOUMIS' && <span style={{ display:'flex', alignItems:'center', gap:5, color:'#0891B2', fontWeight:700, fontSize:'0.8rem' }}>En attente</span>}
            {statut === 'REJETE' && <span style={{ display:'flex', alignItems:'center', gap:5, color:'#DC2626', fontWeight:700, fontSize:'0.8rem' }}><XCircle size={14}/> Rejeté</span>}
          </div>
        )}
      </div>

      {/* Rejection notice */}
      {isView && statut === 'REJETE' && arret?.commentaire_rh && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 16px',
          borderRadius: 10, marginBottom: 20,
          background: 'rgba(220,38,38,0.07)', border: '1px solid rgba(220,38,38,0.22)',
        }}>
          <AlertCircle size={16} style={{ color: '#DC2626', flexShrink: 0, marginTop: 1 }} />
          <div>
            <div style={{ fontWeight: 700, color: '#DC2626', fontSize: '0.8rem', marginBottom: 2 }}>Motif du rejet</div>
            <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>{arret.commentaire_rh}</div>
          </div>
        </div>
      )}

      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: 28 }}>
        <form onSubmit={handleSubmit}>

          {/* Dates */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
            <div>
              <label style={labelStyle}>Date de début *</label>
              <input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)}
                required disabled={isView}
                style={{ ...inpStyle, opacity: isView ? 0.7 : 1 }}
                onFocus={e => !isView && (e.target.style.borderColor = '#DC2626')}
                onBlur={e => (e.target.style.borderColor = 'var(--border)')} />
            </div>
            <div>
              <label style={labelStyle}>Date de fin *</label>
              <input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)}
                required disabled={isView} min={dateDebut}
                style={{ ...inpStyle, opacity: isView ? 0.7 : 1 }}
                onFocus={e => !isView && (e.target.style.borderColor = '#DC2626')}
                onBlur={e => (e.target.style.borderColor = 'var(--border)')} />
            </div>
          </div>

          {/* Demi-journées */}
          <div style={{ display: 'flex', gap: 20, marginBottom: 20 }}>
            {[
              { val: departMidi, set: setDepartMidi, label: 'Départ à 12h (demi-journée)' },
              { val: retourMidi, set: setRetourMidi, label: 'Retour à 14h (demi-journée)' },
            ].map(({ val, set, label }) => (
              <label key={label} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: isView ? 'default' : 'pointer' }}>
                <input type="checkbox" checked={val} onChange={e => !isView && set(e.target.checked)}
                  disabled={isView}
                  style={{ width: 16, height: 16, accentColor: '#DC2626' }} />
                <span style={{ fontSize: '0.875rem', color: 'var(--text-primary)' }}>{label}</span>
              </label>
            ))}
          </div>

          {/* Description */}
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Description <span style={{ fontWeight: 400, textTransform: 'none' }}>(optionnelle)</span></label>
            <textarea value={description} onChange={e => !isView && setDescription(e.target.value)}
              disabled={isView} rows={3} placeholder="Précisions…"
              style={{ ...inpStyle, resize: 'vertical', opacity: isView ? 0.7 : 1 }}
              onFocus={e => !isView && (e.target.style.borderColor = '#DC2626')}
              onBlur={e => (e.target.style.borderColor = 'var(--border)')} />
          </div>

          {/* Justificatifs */}
          <div style={{ marginBottom: isView ? 0 : 24 }}>
            <label style={labelStyle}>
              Justificatifs {!isView && <span style={{ color: '#DC2626' }}>* (obligatoires)</span>}
            </label>

            {/* Fichiers uploadés (mode view) */}
            {isView && arret?.justificatifs?.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
                {arret.justificatifs.map(j => (
                  <UploadedFileBadge key={j.id} justif={j} arretId={id}
                    onDeleted={jid => setArret(prev => ({
                      ...prev, justificatifs: prev.justificatifs.filter(x => x.id !== jid)
                    }))}
                    canDelete={statut !== 'VALIDE'}
                  />
                ))}
              </div>
            )}

            {/* Ajouter justificatif (mode view, si pas validé) */}
            {isView && statut !== 'VALIDE' && (
              <label style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '7px 14px', borderRadius: 8, cursor: 'pointer',
                border: '1px dashed var(--border-strong)', background: 'var(--bg-elevated)',
                fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 4,
              }}>
                <Plus size={13} /> Ajouter un justificatif
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }}
                  onChange={handleAddJustificatif} />
              </label>
            )}

            {/* Zone de drop (mode création) */}
            {!isView && (
              <>
                <div
                  onClick={() => fileRef.current?.click()}
                  onDrop={e => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
                  onDragOver={e => e.preventDefault()}
                  style={{
                    border: `2px dashed ${files.length > 0 ? '#DC2626' : 'var(--border-strong)'}`,
                    borderRadius: 10, padding: '20px 16px', textAlign: 'center', cursor: 'pointer',
                    background: files.length > 0 ? 'rgba(220,38,38,0.04)' : 'var(--bg-elevated)',
                    marginBottom: files.length > 0 ? 10 : 0,
                  }}>
                  <Upload size={22} style={{ margin: '0 auto 8px', color: 'var(--text-muted)' }} />
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                    Cliquer ou déposer des fichiers ici<br />
                    <span style={{ fontSize: '0.72rem' }}>PDF, image — max 10 Mo par fichier</span>
                  </div>
                  <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" multiple
                    style={{ display: 'none' }}
                    onChange={e => addFiles(e.target.files)} />
                </div>
                {files.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {files.map((f, i) => (
                      <FileBadge key={i} file={f} onRemove={() => setFiles(prev => prev.filter((_, j) => j !== i))} />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Bouton submit (création seulement) */}
          {!isView && (
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
              <button type="button" onClick={() => navigate('/arrets-maladie')}
                style={{ padding: '10px 22px', borderRadius: 9, border: '1.5px solid var(--border)',
                         background: 'transparent', cursor: 'pointer', color: 'var(--text-secondary)',
                         fontWeight: 500, fontSize: '0.875rem' }}>
                Annuler
              </button>
              <button type="submit" disabled={loading}
                style={{ padding: '10px 24px', borderRadius: 9, border: 'none',
                         background: loading ? 'var(--text-muted)' : '#DC2626',
                         color: '#fff', cursor: loading ? 'not-allowed' : 'pointer',
                         fontWeight: 700, fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 7 }}>
                {loading ? (
                  <>
                    <span style={{ width: 14, height: 14, borderRadius: '50%',
                      border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff',
                      display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
                    Dépôt…
                  </>
                ) : <><Upload size={14} /> Déposer l'arrêt</>}
              </button>
            </div>
          )}
        </form>

        {/* Actions admin (mode view) */}
        {isView && isAdmin && statut === 'SOUMIS' && (
          <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid var(--border)' }}>
            <div style={{ fontWeight: 700, fontSize: '0.875rem', color: 'var(--text-primary)', marginBottom: 12 }}>
              Actions de validation
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => { setValidAction('REJETE'); setShowValidModal(true); }}
                style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 20px', borderRadius: 9,
                         border: '1.5px solid rgba(220,38,38,0.35)', background: 'rgba(220,38,38,0.07)',
                         color: '#DC2626', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' }}>
                <XCircle size={15} /> Rejeter
              </button>
              <button onClick={() => { setValidAction('VALIDE'); setShowValidModal(true); }}
                style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 20px', borderRadius: 9,
                         border: 'none', background: '#059669', color: '#fff',
                         cursor: 'pointer', fontWeight: 700, fontSize: '0.875rem' }}>
                <CheckCircle2 size={15} /> Valider
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal validation */}
      {showValidModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}
             onClick={e => e.target === e.currentTarget && setShowValidModal(false)}>
          <div style={{ background: 'var(--bg-card)', borderRadius: 14, width: '100%', maxWidth: 440,
                        border: '1px solid var(--border-strong)', padding: 24 }}>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-primary)', marginBottom: 4 }}>
              {validAction === 'VALIDE' ? '✓ Valider l\'arrêt maladie' : '✗ Rejeter l\'arrêt maladie'}
            </div>
            {validAction === 'VALIDE' && (
              <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: 16 }}>
                La feuille de temps sera automatiquement mise à jour.
              </div>
            )}
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)',
                             textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
              Commentaire {validAction === 'REJETE' && <span style={{ color: '#DC2626' }}>*</span>}
            </label>
            <textarea value={commentaire} onChange={e => setCommentaire(e.target.value)}
              rows={3} placeholder={validAction === 'REJETE' ? 'Motif du rejet…' : 'Commentaire optionnel…'}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, boxSizing: 'border-box',
                       border: '1.5px solid var(--border)', background: 'var(--bg-elevated)',
                       color: 'var(--text-primary)', fontSize: '0.875rem', resize: 'vertical' }} />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={() => setShowValidModal(false)}
                style={{ padding: '9px 18px', borderRadius: 9, border: '1.5px solid var(--border)',
                         background: 'transparent', cursor: 'pointer', color: 'var(--text-secondary)',
                         fontWeight: 500, fontSize: '0.875rem' }}>
                Annuler
              </button>
              <button onClick={handleValidate}
                disabled={validAction === 'REJETE' && !commentaire}
                style={{ padding: '9px 20px', borderRadius: 9, border: 'none', cursor: 'pointer',
                         background: validAction === 'VALIDE' ? '#059669' : '#DC2626',
                         color: '#fff', fontWeight: 700, fontSize: '0.875rem',
                         opacity: (validAction === 'REJETE' && !commentaire) ? 0.5 : 1 }}>
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
